import React, { useState } from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { KTIcon } from '../../../../../_metronic/helpers'
import { postLedgerDeposit } from './core/api'
import { overdraftAfterWithdraw } from './core/overdraft'
import { ILedgerDepositResult, LedgerBalanceOp, LedgerWalletOwnerType } from './core/types'
import styles from './LedgerDepositModal.module.css'

const AppSwal = withReactContent(Swal)

interface Props {
  ownerType: LedgerWalletOwnerType
  id: number
  isOpen: boolean
  onClose: () => void
  onSuccess: (result: ILedgerDepositResult) => void
  availableBalance?: string
  currency?: 'RUB' | 'USDT' | null
}

type Step = 'choose' | 'form'

const LedgerDepositModal: React.FC<Props> = ({
  ownerType,
  id,
  isOpen,
  onClose,
  onSuccess,
  availableBalance,
  currency,
}) => {
  const [step, setStep] = useState<Step>('choose')
  const [op, setOp] = useState<LedgerBalanceOp>('deposit')
  const [amount, setAmount] = useState('')
  const [source, setSource] = useState('')
  const [externalReference, setExternalReference] = useState('')
  const [amountError, setAmountError] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [canRetry, setCanRetry] = useState(false)
  const [loading, setLoading] = useState(false)

  const isWithdraw = op === 'withdraw'
  // A merchant withdrawal may take the balance below zero; a cash box one is
  // refused above the available balance by the ledger.
  const isMerchant = ownerType === 'merchant'
  const overdraft =
    isWithdraw && isMerchant ? overdraftAfterWithdraw(availableBalance, amount) : null

  const resetForm = () => {
    setAmount('')
    setSource('')
    setExternalReference('')
    setAmountError(null)
    setServerError(null)
    setCanRetry(false)
  }

  const reset = () => {
    resetForm()
    setStep('choose')
    setOp('deposit')
  }

  const close = () => {
    reset()
    onClose()
  }

  const chooseOp = (next: LedgerBalanceOp) => {
    resetForm()
    setOp(next)
    setStep('form')
  }

  const backToChoose = () => {
    resetForm()
    setStep('choose')
  }

  const onAmountChange = (value: string) => {
    setAmount(value)
    setAmountError(null)
    setServerError(null)
    setCanRetry(false)
  }

  const validate = (): boolean => {
    const parsed = parseFloat(amount)
    if (!amount || isNaN(parsed) || parsed <= 0) {
      setAmountError('Введите сумму больше нуля')
      return false
    }
    // Validate the value as it will actually be sent (scale 4): reject inputs
    // that round to zero (e.g. 0.00004 → "0.0000") or degrade to scientific
    // notation (|x| >= 1e21 → "1e+21") — both would be rejected by the ledger.
    const magnitude = parsed.toFixed(4)
    if (parseFloat(magnitude) === 0 || /[eE]/.test(magnitude)) {
      setAmountError('Введите корректную сумму (до 4 знаков после запятой)')
      return false
    }
    return true
  }

  const submit = async () => {
    if (!validate()) return
    setLoading(true)
    setServerError(null)
    setCanRetry(false)

    const metadata: Record<string, string> = {}
    if (source.trim()) metadata.source = source.trim()
    if (externalReference.trim()) metadata.external_reference = externalReference.trim()

    // Sign is driven by the chosen operation, never typed by the user: a
    // withdrawal sends a negative amount to the same endpoint (CPAYMENT-1573).
    const magnitude = parseFloat(amount).toFixed(4)
    const signedAmount = isWithdraw ? `-${magnitude}` : magnitude

    try {
      // depositId is generated by the backend (idempotency key is a backend
      // concern per the ledger contract); the UI only sends amount + metadata.
      const res = await postLedgerDeposit(ownerType, id, {
        amount: signedAmount,
        ...(Object.keys(metadata).length ? { metadata } : {}),
      })

      if (res.data.success && res.data.data) {
        const result = res.data.data
        onSuccess(result)
        close()
        if (result.status === 'ALREADY_POSTED') {
          AppSwal.fire({
            icon: 'info',
            title: 'Операция уже была проведена',
            text: `Баланс: ${result.balanceAfter} ${result.currency ?? ''}`.trim(),
          })
        } else {
          AppSwal.fire({
            icon: 'success',
            title: isWithdraw ? 'Списано' : 'Зачислено',
            text: `Баланс: ${result.balanceAfter} ${result.currency ?? ''}`.trim(),
            timer: 2500,
            showConfirmButton: false,
          })
        }
      } else {
        setServerError(isWithdraw ? 'Не удалось списать средства' : 'Не удалось зачислить средства')
      }
    } catch (e: any) {
      // The global axios interceptor rejects with error.response for
      // /ledger-deposits (carve-out), so e is the response object. Insufficient
      // balance comes back as 400 with errors.general_error (backend maps the
      // ledger INSUFFICIENT_BALANCE to a bad request).
      const status: number | undefined = e?.status
      const message: string | undefined = e?.data?.errors?.general_error ?? e?.data?.errors?.amount
      if (status === 500) {
        setServerError('Ledger недоступен. Попробуйте повторить.')
        setCanRetry(true)
      } else if (status === 409) {
        setServerError(message ?? 'Операция с тем же ключом уже проведена с другими параметрами')
      } else {
        setServerError(
          message ??
            (isWithdraw ? 'Произошла ошибка при списании' : 'Произошла ошибка при зачислении')
        )
      }
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  const submitLabel = loading
    ? isWithdraw
      ? 'Списание…'
      : 'Зачисление…'
    : canRetry
    ? 'Повторить'
    : isWithdraw
    ? 'Списать'
    : 'Зачислить'

  return (
    <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              {step === 'choose'
                ? 'Операция над балансом'
                : isWithdraw
                ? 'Списание средств'
                : 'Пополнение баланса'}
            </h5>
            <button className='btn-close' onClick={close} disabled={loading} />
          </div>

          {step === 'choose' ? (
            <>
              <div className='modal-body'>
                <p className='text-muted fs-7 mb-5'>Что вы хотите сделать с балансом кошелька?</p>
                <div className='d-flex flex-column gap-3'>
                  <button
                    type='button'
                    className='btn btn-light-primary btn-lg d-flex align-items-center justify-content-center'
                    onClick={() => chooseOp('deposit')}
                  >
                    <KTIcon iconName='plus' className='fs-2 me-2' />
                    Зачислить
                  </button>
                  <button
                    type='button'
                    className='btn btn-light-danger btn-lg d-flex align-items-center justify-content-center'
                    onClick={() => chooseOp('withdraw')}
                  >
                    <KTIcon iconName='minus' className='fs-2 me-2' />
                    Списать
                  </button>
                </div>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-light' onClick={close}>
                  Отмена
                </button>
              </div>
            </>
          ) : (
            <>
              <div className='modal-body'>
                <button
                  type='button'
                  className={`btn btn-link btn-sm p-0 mb-4 text-gray-700 text-hover-primary text-decoration-underline fs-8 ${styles.backLink}`}
                  onClick={backToChoose}
                  disabled={loading}
                >
                  <KTIcon iconName='arrow-left' className='fs-7 me-1' />К выбору операции
                </button>

                {isWithdraw && availableBalance != null && (
                  <div className='alert alert-light-primary d-flex align-items-center py-3 mb-4'>
                    <KTIcon iconName='wallet' className='fs-3 me-2' />
                    <span className='fs-7'>
                      {isMerchant ? 'Доступный баланс' : 'Доступно к списанию'}:{' '}
                      <b>{availableBalance}</b> {currency ?? ''}
                    </span>
                  </div>
                )}

                <div className='mb-4'>
                  <label className='form-label required'>Сумма</label>
                  <input
                    type='number'
                    step='0.01'
                    min='0'
                    className={`form-control ${amountError ? 'is-invalid' : ''}`}
                    placeholder='Пример: 1000.00'
                    value={amount}
                    onChange={(e) => onAmountChange(e.target.value)}
                  />
                  {amountError && <div className='invalid-feedback'>{amountError}</div>}
                  {overdraft && (
                    <div
                      className='alert alert-warning py-2 px-3 mt-3 mb-0 fs-7'
                      data-testid='ledger-overdraft-warning'
                    >
                      Баланс уйдёт в минус на <b>{overdraft}</b> {currency ?? ''}
                    </div>
                  )}
                </div>
                <div className='mb-4'>
                  <label className='form-label'>Источник</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Например: bank_transfer'
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                  />
                </div>
                <div className='mb-2'>
                  <label className='form-label'>Внешний референс</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='ID внешней операции'
                    value={externalReference}
                    onChange={(e) => setExternalReference(e.target.value)}
                  />
                </div>
                {serverError && <div className='text-danger fs-7 mt-3'>{serverError}</div>}
              </div>
              <div className='modal-footer'>
                <button className='btn btn-light' onClick={close} disabled={loading}>
                  Отмена
                </button>
                <button
                  className={`btn ${isWithdraw ? 'btn-danger' : 'btn-primary'}`}
                  onClick={submit}
                  disabled={loading}
                >
                  {submitLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default LedgerDepositModal
